<template>
  <div class="modal-card">
    <header class="modal-card-head">
      <p class="modal-card-title is-size-6 has-text-weight-semibold">Configure Located Vehicle</p>
    </header>
    <section class="modal-card-body locate-deal-section deal-status">
      <error-display-component :serverErrors="serverErrors" :serverErrorMessage="serverErrorMessage"></error-display-component>
      <make-model-selector
        :label="makeModelSelectorTitle"
        data-vv-as="selected vehicle"
        name="selectedMakeModel"
        :v-validate="{'required': true}"
        v-model="selectedMakeModel"
        :stockType="selectedStockType"
        :forceColorSelection="true"
        :showNotes="false"
        :showTags="false"
        :showAlternateColors="false"
        :showSourceSelector="true"
        :showVinSelector="true"
        :showCostSelector="true"
        :showAskingPriceSelector="true"
        :showTypeSelector="true"
        :showMileageSelector="true"
        :captureTitleStatus="true"
        :isLocated="true"
        :forceVinCollection="true"
        :useModal="false"
        :purchasedFromType="currentInventory.purchasedFromType"
        :inventoryType="inventoryTypeStatus">
      </make-model-selector>
      <span v-show="errors.has('selectedMakeModel')" class="help is-danger"><i v-show="errors.has('selectedMakeModel')" class="fas fa-exclamation-triangle"></i> {{ errors.first('selectedMakeModel') }}</span>
    </section>
    <footer class="modal-card-foot">
      <button class="button is-danger" type="button" @click="onCancel()">Close</button>
      <button :disabled="isUploadingFile" class="button is-primary" :class="{'is-loading': isSaving }" @click="onSubmitForm()">Save</button>
    </footer>

    <b-loading :is-full-page="true" :active.sync="isLoading" :canCancel="false"></b-loading>

    <auto-add-inventory-defaultcosts-component
      v-model="deal"
      :key="deal.id">
    </auto-add-inventory-defaultcosts-component>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import inventoryService from '@/services/inventoryService'
import dealService from '@/services/dealService'
import DealerSelector from '@/components/generic/DealerSelector'
import validateBeforeSubmitMixin from '@/mixins/generic/validateBeforeSubmit'
import SectionHeader from '@/components/generic/SectionHeader'
import moment from 'moment'
import MakeModelSelector from '@/components/generic/MakeModelSelector'
import ErrorDisplayComponent from '@/components/generic/ErrorDisplayComponent'
import eventBus from '@/eventBus'
import AutoAddInventoryDefaultCostsComponent from '@/components/generic/inventory/AutoAddInventoryDefaultCostsComponent'
import { setInventoryAttribute, setInventoryAttributeRow, readOwnedInventoryAttribute } from '@/utils/inventory_attributes'

const mapConfigGetters = createNamespacedHelpers('config').mapGetters
const mapVehicleMakeGetters = createNamespacedHelpers('vehicleMake').mapGetters

export default {
  name: 'LocateDealCapture',
  mixins: [validateBeforeSubmitMixin],
  components: {
    'dealer-selector': DealerSelector,
    'section-header': SectionHeader,
    'make-model-selector': MakeModelSelector,
    'error-display-component': ErrorDisplayComponent,
    'auto-add-inventory-defaultcosts-component': AutoAddInventoryDefaultCostsComponent
  },
  props: {
    value: {
      type: Object,
      default: null
    },
    isLocatedCaptureActive: {
      type: Boolean,
      default: true
    },
    statusProgressionWizardActive: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      currentInventory: {
      },
      selectedDealer: null,
      selectedInventory: null,
      locateVehicleIsFound: true,
      selectedStockType: null,
      selectedMakeModel: null,
      serverErrors: [],
      serverErrorMessage: '',
      isSaving: false,
      isLoading: false,
      deal: this.value,
      isUploadingFile: false
    }
  },
  computed: {
    ...mapConfigGetters(['definedTypes']),
    soldItems: function () {
      return (this.deal && this.deal.items) ? this.deal.items.filter((x) => x.dealItemType === 'Sold') : []
    },
    firstSoldItemInventory: function () {
      return this.soldItems.length > 0 ? this.soldItems[0].forInventory : null
    },
    overview: function () {
      if (!this.currentInventory) {
        return ''
      } else {
        var result = ''
        if (this.currentInventory.vinNo !== '') {
          result += 'VIN: ' + this.currentInventory.vinNo
        } else {
          result += 'VIN: Pending'
        }
        if (this.currentInventory.stockNo !== '') {
          result += ', STK: ' + this.currentInventory.stockNo
        } else {
          result += ', STK - Pending'
        }
        if (this.currentInventory.name !== '') {
          result += ', ITEM: ' + this.currentInventory.name
        } else {
          result += ', ITEM: -'
        }
        if (this.currentInventory.type !== '') {
          result += ', TYPE: ' + this.currentInventory.type
        } else {
          result += ', TYPE: -'
        }
        if (this.currentInventory.cpoStatus !== '') {
          result += ', CPO: ' + this.currentInventory.cpoStatus
        } else {
          result += ', CPO: -'
        }
        return result
      }
    },
    makeModelSelectorTitle: function () {
      return 'Located Vehicle'
    },
    ...mapVehicleMakeGetters(['allVehicleMakes']),
    availableVehicleMakes () {
      return this.allVehicleMakes
    },
    inventoryTypeStatus: function () {
      return this.definedTypes.inventoryTypes.find((x) => (x.name === this.currentInventory.type || x.altName === this.currentInventory.type))
    }
  },
  methods: {
    onCancel: function () {
      this.$emit('update:isLocatedCaptureActive', false)
      this.$emit('update:statusProgressionWizardActive', true)
    },
    onComplete: function () {
      this.$emit('update:isLocatedCaptureActive', false)
      eventBus.$emit('update-deal-status-to-awaitingarrival')
    },
    reset: function () {
      this.selectedDealer = null
      this.selectedPossessionStatus = null
      this.selectedMakeModel = null
      this.selectedSource = null
      this.selectedTypeStatus = null
      this.selectedCpoStatus = null
      this.selectedCustomer = null
      this.selectedWholeSaler = null
      this.selectedModel = null
      this.selectedDamageStatus = null
      this.selectedKeyStatus = null
      this.selectedBooksStatus = null
      this.selectedCarCoverStatus = null
      this.selectedBatteryChargerStatus = null
      this.selectedTitleStatus = null
      this.selectedWindowStickerStatus = null
      this.lastServiceDate = null
      this.warrantyStartDate = null
      this.warrantyExpirationDate = null
      this.selectedLocation = null
      this.isUsageVerificationActive = false
      this.active = 0
      this.damageNote = ''
      this.damageFiles = []
      this.titleFiles = []
      this.windowStickerFiles = []
      this.inventoryPhotoFiles = []
      this.windowStickerPdfFiles = []
      this.notes = []
    },
    loadInventory: function (id) {
      this.reset()
      this.currentInventoryId = id
      this.isLoading = true
      this.serverErrorMessage = ''

      inventoryService.single(this.currentInventoryId).then(response => {
        this.currentInventory = {
          ...response.inventory
        }
        this.selectedDealer = {id: this.currentInventory.dealerId, name: this.currentInventory.dealerName}
        this.initialInventoryModel()
        this.isLoading = false
      }).catch((error) => {
        this.onError(error)
      })
    },
    setInitialDealValues () {
      if (this.deal) {
        this.selectedStockType = this.definedTypes.dealStockTypes.find((x) => x.name === this.deal.stockType || x.altName === this.deal.stockType)
      }
    },
    initialInventoryModel: function () {
      if (!this.currentInventory) {
        return
      }

      this.selectedInventory = this.currentInventory
      if (this.selectedInventory.attributes) {
        var exteriorColor = this.selectedInventory.attributes.find((x) => x.name === 'ExteriorColor')
        var interiorColor = this.selectedInventory.attributes.find((x) => x.name === 'InteriorColor')
        var vehicleStyle = this.selectedInventory.attributes.find((x) => x.name === 'Style')
        //⚠ The OWNED row, newest-first and skipping blanks. The old bare find took an arbitrary
        //row of an unordered bag, so the box showed an undefined one of N damage events.
        var ownedDamageNote = readOwnedInventoryAttribute(this.selectedInventory, 'DamageNotes')
        var damageNote = ownedDamageNote.value
        var vehicleTrim = this.selectedInventory.attributes.find((x) => x.name === 'Trim')
        var searchType = this.selectedInventory.attributes.find((x) => x.name === 'SearchType')
        var altExteriorColors = this.selectedInventory.attributes.find((x) => x.name === 'AlternateExteriorColors')
        var altInteriorColors = this.selectedInventory.attributes.find((x) => x.name === 'AlternateInteriorColors')
        var searchTags = this.selectedInventory.attributes.find((x) => x.name === 'SearchTags')
        var searchNotes = this.selectedInventory.attributes.find((x) => x.name === 'SearchNotes')
      }

      var purchaseCost = this.selectedInventory.purchaseCost
      var usage = this.selectedInventory.usage
      var askingPrice = this.selectedInventory.askingPrice

      let status = this.selectedInventory.status
      let selectedPossessionStatus = this.definedTypes.possessionStatuses.find((x) => (x.name === this.selectedInventory.possessionStatus || x.altName === this.selectedInventory.possessionStatus))
      var locateVehicleIsFound = true
      // if (this.selectedStockType.name === 'Locate') {
      if (status === 'Requested') {
        if (selectedPossessionStatus.name === 'Locating') {
          locateVehicleIsFound = false
        }
      }
      // }
      this.locateVehicleIsFound = locateVehicleIsFound
      let selectedTitleStatus = this.definedTypes.titleStatusOptions.options.find((x) => (x.name === this.selectedInventory.titleStatus || x.altName === this.selectedInventory.titleStatus))
      let selectedDamageStatus = this.selectedInventory.isDamaged === null ? false : this.selectedInventory.isDamaged
      let warrantyExpirationDate = this.selectedInventory.warrantyExpirationDate ? new Date(this.selectedInventory.warrantyExpirationDate) : null
      let goodUntilDate = this.selectedInventory.goodUntilDate ? new Date(this.selectedInventory.goodUntilDate) : null
      let vinNo = this.selectedInventory.vinNo
      let accNo = this.selectedInventory.accNo
      let payoffAmount = this.selectedInventory.payoffAmount
      let selectedSource = this.selectedInventory.purchasedFromType === null ? null : this.definedTypes.allowedSourceTypes.options.find((x) => (x.name === this.selectedInventory.purchasedFromType || x.altName === this.selectedInventory.purchasedFromType))
      var selectedBank = null
      if (this.selectedInventory.bankId !== null && this.selectedInventory.bankId !== 0) {
        selectedBank = {
          id: this.selectedInventory.bankId,
          name: this.selectedInventory.bankName
        }
      }
      var selectedWholesaler = null
      var selectedCustomer = null
      if (selectedSource && selectedSource.id > this.definedTypes.allowedSourceTypes.Factory) {
        if (selectedSource.id === this.definedTypes.allowedSourceTypes.Wholesaler) {
          var supplier = {
            id: this.selectedInventory.purchasedFromId,
            name: this.selectedInventory.purchasedFromName
          }
          selectedWholesaler = supplier
        } else {
          var customer = {
            id: this.selectedInventory.purchasedFromId,
            fullName: this.selectedInventory.purchasedFromName
          }
          selectedCustomer = customer
        }
      }

      let titleFiles = []
      this.selectedInventory.attachments.forEach((x) => {
        x.loaded = true
        x.imageData = x.fileLocation
        if (x.classType === 'Title') {
          titleFiles.push(x)
        }
      })

      let damageFiles = []
      this.selectedInventory.attachments.forEach((x) => {
        x.loaded = true
        x.imageData = x.fileLocation
        if (x.classType === 'Damage') {
          damageFiles.push(x)
        }
      })

      var vehicleAge = moment().year() - (+this.selectedInventory.vehicleProductionYear)
      var cpoAllowed = true
      var autoCpoStatus = 'Ineligible'

      if (this.selectedDealer && vehicleAge >= 0 && vehicleAge <= this.selectedDealer.autoCpoAgeLimit) {
        cpoAllowed = true
        autoCpoStatus = 'Cpo'
      } else if (this.selectedDealer && vehicleAge > this.selectedDealer.autoCpoAgeLimit && vehicleAge <= this.selectedDealer.cpoQualifyingAge) {
        cpoAllowed = true
        autoCpoStatus = 'Eligible'
      }

      let selectedMake = this.selectedInventory.vehicleMakeId > 0 ? this.allVehicleMakes.find((x) => x.id === this.selectedInventory.vehicleMakeId) : null
      let selectedModel = selectedMake && selectedMake.versions ? (selectedMake.versions.find((x) => x.id === this.selectedInventory.vehicleMakeVersionId)) : null

      this.selectedMakeModel = {
        exteriorColor: exteriorColor ? exteriorColor.value : '',
        interiorColor: interiorColor ? interiorColor.value : '',
        style: vehicleStyle ? vehicleStyle.value : '',
        makeId: this.selectedInventory.vehicleMakeId,
        modelId: this.selectedInventory.vehicleMakeVersionId,
        productionYear: this.selectedInventory.vehicleProductionYear,
        customMakeName: this.selectedInventory.customMake === '' ? null : this.selectedInventory.customMake,
        customModelName: this.selectedInventory.customModel === '' ? null : this.selectedInventory.customModel,
        customMakeModel: this.selectedInventory.customVehicleMakeVersion,
        trim: vehicleTrim ? vehicleTrim.value : '',
        selectedMake: selectedMake,
        selectedModel: selectedModel,
        vehicleAge: vehicleAge,
        cpoAllowed: cpoAllowed,
        autoCpoStatus: autoCpoStatus,
        purchaseCost: purchaseCost,
        usage: usage,
        askingPrice: askingPrice,
        locateType: (searchType && searchType.value !== '') ? this.definedTypes.allowedLocateTypes.options.find((x) => x.name === searchType.value) : null,
        altExteriorColors: (altExteriorColors && altExteriorColors.value && altExteriorColors.value.length > 0) ? altExteriorColors.value.split(',') : [],
        altInteriorColors: (altInteriorColors && altInteriorColors.value && altInteriorColors.value.length > 0) ? altInteriorColors.value.split(',') : [],
        tags: (searchTags && searchTags.value && searchTags.value.length > 0) ? searchTags.value.split(',') : [],
        notes: searchNotes ? searchNotes.value : '',
        source: selectedSource,
        wholesaler: selectedWholesaler,
        customer: selectedCustomer,
        titleStatus: selectedTitleStatus,
        warrantyExpirationDate: warrantyExpirationDate,
        selectedBank: selectedBank,
        accountNo: accNo,
        payoffAmount: payoffAmount,
        goodUntilDate: goodUntilDate,
        isDamaged: selectedDamageStatus,
        vinNo: vinNo,
        titleFiles: titleFiles,
        damageFiles: damageFiles,
        damageNote: damageNote,
        //Carried so the watcher below can write back to the row it read, by id.
        damageNoteId: ownedDamageNote.id,
        transmissionType: this.currentInventory.transmissionType
      }
    },
    onError: function (error) {
      this.serverErrorMessage = error.message
      this.isLoading = false
      this.isSaving = false
    },
    onSave: function () {
      this.isSaving = true
      this.serverErrorMessage = ''
      this.serverErrors = []
     
      // we need to call this to add any missing costs to inventory in items model
      eventBus.$emit('on-add-missing-costs')

      dealService.update(this.deal).then(response => {
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          let responseSoldItems = response.deal.items.filter((x) => x.dealItemType === 'Sold')
          if (responseSoldItems.length > 0 && this.soldItems.length > 0) {
            this.soldItems[0].forInventory = responseSoldItems[0].forInventory
          }

          dealService.addMissingDefaultCosts(this.deal).then(response => {
            this.isSaving = false
            if (response.errors) {
              this.serverErrors = response.errors
            } else {
              this.onComplete()
            }
          }).catch((error) => {
            this.serverErrorMessage = error.message
            this.isSaving = false
          })
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    onSubmitForm () {
      eventBus.$emit('submit-make-model-form')
    },
    fileUploadsComplete: function () {
      this.isUploadingFile = false
    },
    fileUploadsStarted: function () {
      this.isUploadingFile = true
    }
  },
  watch: {
    selectedMakeModel: {
      handler: function (newValue, oldValue) {
        var stockNo = ''
        var vinNo = ''

        if (this.selectedInventory !== null) {
          stockNo = this.selectedInventory.stockNo
          vinNo = this.selectedInventory.vinNo
        }

        stockNo = (newValue.stockNo) ? newValue.stockNo : stockNo
        vinNo = (newValue.vinNo) ? newValue.vinNo : vinNo

        // let newInventoryStatus = this.selectedStockType.name === 'Locate' ? (this.locateVehicleIsFound === true ? 'Active' : 'Requested') : 'Active'
        let newInventoryStatus = 'Active'
        let newInventoryType = (newValue.locateType && newValue.locateType.name === 'New') ? 'New' : 'Purchased'
        // let newInventoryPossessionStatus = this.selectedStockType.name === 'Locate' ? (this.locateVehicleIsFound === true ? 'Inbound' : 'Locating') : 'Inbound'
        let newInventoryPossessionStatus = 'Inbound'
        let newInventoryDealerId = (this.selectedDealer) ? this.selectedDealer.id : null
        let newInventoryPurchasedFromType = (newValue.source && newValue.source !== '') ? newValue.source.altName : null
        let newInventoryPurchasedFromId = newValue.wholesaler ? newValue.wholesaler.id : (newValue.customer ? newValue.customer.id : null)
        let newTitleStatus = newValue.titleStatus ? newValue.titleStatus.name : null
        let newWarrantyExpirationDate = newValue.warrantyExpirationDate
        let newBankId = newValue.selectedBank ? newValue.selectedBank.id : null
        let newAccountNo = newValue.accountNo
        let newPayOffAmount = newValue.payoffAmount
        let newGoodUntilDate = newValue.goodUntilDate
        let isDamaged = newValue.isDamaged

        var inventoryModel = {
          ...this.selectedInventory,
          dealerId: newInventoryDealerId,
          stockNo: stockNo,
          vinNo: vinNo,
          isDamaged: isDamaged,
          type: newInventoryType,
          purchasedFromType: newInventoryPurchasedFromType,
          purchasedFromId: newInventoryPurchasedFromId,
          status: newInventoryStatus,
          possessionStatus: newInventoryPossessionStatus,
          purchaseCost: newValue.purchaseCost,
          usage: newValue.usage,
          askingPrice: newValue.askingPrice,
          bankId: newBankId,
          accNo: newAccountNo,
          goodUntilDate: newGoodUntilDate,
          payoffAmount: newPayOffAmount,
          titleStatus: newTitleStatus,
          warrantyExpirationDate: newWarrantyExpirationDate,
          vehicleMakeId: !newValue || newValue.makeId === -1 ? null : newValue.makeId,
          vehicleMakeVersionId: !newValue || newValue.modelId === -1 ? null : newValue.modelId,
          customVehicleMakeVersion: newValue ? newValue.customMakeModel : '',
          locationId: null,
          attachments: [],
          transmissionType: newValue.transmissionType
        }

        if (newValue.titleFiles.length > 0) {
          inventoryModel.attachments.push(...newValue.titleFiles)
        }

        if (newValue.damageFiles.length > 0) {
          inventoryModel.attachments.push(...newValue.damageFiles)
        }

        // Set the deal sale price to the sale asking price captured in the make model selector
        this.soldItems[0].salePrice = this.soldItems.length > 0 && newValue.askingPrice ? newValue.askingPrice : 0

        if (newValue !== null) {
          setInventoryAttribute(inventoryModel, 'ExteriorColor', newValue.exteriorColor)
          setInventoryAttribute(inventoryModel, 'InteriorColor', newValue.interiorColor)
          //⚠ By ID, never by name. This fires from the selectedMakeModel watcher - on every
          //selector change, not just a save - so a name-match here deleted or overwrote an
          //arbitrary damage event whenever the note had not hydrated.
          setInventoryAttributeRow(inventoryModel, 'DamageNotes', newValue.damageNoteId, newValue.damageNote)
          setInventoryAttribute(inventoryModel, 'Style', newValue.style)
          setInventoryAttribute(inventoryModel, 'Trim', newValue.trim)
          setInventoryAttribute(inventoryModel, 'ProductionYear', newValue.productionYear)
          setInventoryAttribute(inventoryModel, 'Make', newValue.customMakeName)
          setInventoryAttribute(inventoryModel, 'Model', newValue.customModelName)

          setInventoryAttribute(inventoryModel, 'SearchType', (newValue.locateType && newValue.locateType.name) ? newValue.locateType.name : '')
          setInventoryAttribute(inventoryModel, 'AlternateExteriorColors', (newValue.altExteriorColors && newValue.altExteriorColors.length > 0) ? newValue.altExteriorColors.join(',') : '')
          setInventoryAttribute(inventoryModel, 'AlternateInteriorColors', (newValue.altInteriorColors && newValue.altInteriorColors.length > 0) ? newValue.altInteriorColors.join(',') : '')
          setInventoryAttribute(inventoryModel, 'SearchTags', (newValue.tags && newValue.tags.length > 0) ? newValue.tags.join(',') : '')
          setInventoryAttribute(inventoryModel, 'SearchNotes', (newValue.notes && newValue.notes.length > 0) ? newValue.notes : '')
        }

        if (newValue.locateType && newValue.locateType.id === this.definedTypes.allowedLocateTypes.New && this.soldItems.length > 0) {
          this.soldItems[0].vehicleType = 'New'
        } else if (newValue.locateType && newValue.locateType.id === this.definedTypes.allowedLocateTypes.Used && this.soldItems.length > 0) {
          this.soldItems[0].vehicleType = 'Used'
        }

        if (this.soldItems.length > 0) {
          this.soldItems[0].forInventory = inventoryModel
          this.soldItems[0].modelOverview = newValue.completeOverview
        }

        this.deal.stockType = 'InStock'
      },

      deep: true
    },
    value: function (newValue, oldValue) {
      this.deal = newValue
    }
  },
  mounted: function () {
    if (this.firstSoldItemInventory !== null && this.firstSoldItemInventory.id !== 0) {
      this.loadInventory(this.firstSoldItemInventory.id)
    }
    this.setInitialDealValues()
  },
  created () {
    eventBus.$on('parent-make-model-onSave', this.onSave)
  },
  destroyed () {
    eventBus.$off('parent-make-model-onSave', this.onSave)
  },
  beforeMount () {
    eventBus.$on('file-uploads-completed', this.fileUploadsComplete)
    eventBus.$on('file-uploads-started', this.fileUploadsStarted)
  },
  beforeDestroy () {
    eventBus.$off('file-uploads-completed', this.fileUploadsComplete)
    eventBus.$off('file-uploads-started', this.fileUploadsStarted)
  }
}

</script>

<style scoped>
  .modal-card-head, .modal-card-foot {
    padding: 20px !important
  }

  .locate-deal-section {
    height: calc(100vh - 300px) !important;
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
  }

</style>
